import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import GoogleMapReact from 'google-map-react'
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  ArrowCounterClockwise,
  Path,
  Crosshair,
  CaretRight,
  CaretLeft,
  CaretDown,
  MapPin,
  CheckCircle,
  WarningCircle,
  CornersOut
} from '@phosphor-icons/react'
import { useReducedMotion } from 'framer-motion'
import { MapModalStore } from '../hook/MapModalProvider'
import { API_KEY } from '../../constants/constant'
import { resolveReportGeofence } from './helpers/reportGeofence'
import { bearing, distanceBetweenMarkers } from './helpers/mapGeometry'
import { orderRouteRecords } from './helpers/routeRecords'
import SalesRoutePoint from './SalesRoutePoint'
import SalesRouteLegend from './SalesRouteLegend'
import salesRouteVan from './salesRouteVan.svg'
import { ACTIVITY_REPORT_MAP_COLORS } from '../unified/components/maps/constants/activityReportMap'

const EMPTY = []
// An activity may legitimately be outside its geofence, but a point hundreds of
// kilometres from the customer assigned to that activity is not useful as part
// of a daily sales route. It is normally an invalid device reading and would
// otherwise make fitBounds zoom out to an entire continent.
const MAX_DISTANCE_FROM_CLIENT_METERS = 300000
const MIN_ROUTE_MOVEMENT_METERS = 75
const MARKER_SEPARATION_START_ZOOM = 13
const MARKER_SEPARATION_FULL_ZOOM = 17
const coordinate = (latitude, longitude) => {
  if ([latitude, longitude].some((value) => value == null || String(value).trim() === ''))
    return null
  const lat = Number(latitude),
    lng = Number(longitude)
  return Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180 &&
    lat !== 0 &&
    lng !== 0
    ? { lat, lng }
    : null
}
const time = (activity) => {
  const date = new Date(activity?.created_date)
  return Number.isFinite(date.getTime())
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : 'Sin hora'
}
const statusOf = (point) =>
  !point.client || ![true, false, 0, 1].includes(point.activity.effective_location)
    ? 'unknown'
    : point.activity.effective_location
      ? 'inside'
      : 'outside'
const statusText = {
  inside: 'Dentro de geocerca',
  outside: 'Fuera de geocerca',
  unknown: 'Geocerca sin evaluar'
}
const colors = {
  inside: ACTIVITY_REPORT_MAP_COLORS.geofenceInside,
  outside: ACTIVITY_REPORT_MAP_COLORS.geofenceOutside,
  unknown: ACTIVITY_REPORT_MAP_COLORS.geofenceNoLocation
}
const clientNumber = (point) => point.activity.client_index ?? point.order + 1
const isRouteGPS = (point) =>
  point.gps &&
  (!point.client ||
    distanceBetweenMarkers([point.gps, point.client]) <= MAX_DISTANCE_FROM_CLIENT_METERS)

const routeExtent = (points) =>
  points.reduce(
    (maximum, point, index) =>
      Math.max(
        maximum,
        ...points.slice(index + 1).map((other) => distanceBetweenMarkers([point.gps, other.gps]))
      ),
    0
  )

export default function ShowUserReportMapModalContent({ data = EMPTY, isMobileView = false }) {
  const reduceMotion = useReducedMotion()
  const mapInfoId = useId()
  const { modalMapStyles } = useContext(MapModalStore)
  const [api, setApi] = useState(null)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState(false)
  const [retry, setRetry] = useState(0)
  const [index, setIndex] = useState(0)
  const [partial, setPartial] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [started, setStarted] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [follow, setFollow] = useState(true)
  const [lineVisible, setLineVisible] = useState(true)
  const [mapInfoOpen, setMapInfoOpen] = useState(true)
  const [collapsed, setCollapsed] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [mapZoom, setMapZoom] = useState(0)
  const [panel, setPanel] = useState('detail')
  const [expanded, setExpanded] = useState(false)
  const canvasRef = useRef(null)
  const circleRef = useRef(null)
  const progressRef = useRef(null)
  const overviewRef = useRef(true)
  const records = useMemo(
    () =>
      orderRouteRecords(data)
        .map((point, order) => ({
          ...point,
          order,
          gps: coordinate(point.activity.latitude, point.activity.longitude),
          client: coordinate(point.activity.client_latitude, point.activity.client_longitude)
        })),
    [data]
  )
  const points = useMemo(() => records.filter(isRouteGPS), [records])
  const routeExtentMeters = useMemo(() => routeExtent(points), [points])
  const hasRouteMovement = points.length > 1 && routeExtentMeters > MIN_ROUTE_MOVEMENT_METERS
  const missingGpsCount = records.filter((point) => !point.gps).length
  const excludedGpsCount = records.filter((point) => point.gps && !isRouteGPS(point)).length
  const markerSeparationScale = Math.max(
    0,
    Math.min(
      1,
      (mapZoom - MARKER_SEPARATION_START_ZOOM) /
        (MARKER_SEPARATION_FULL_ZOOM - MARKER_SEPARATION_START_ZOOM)
    )
  )
  const markerOffsets = useMemo(() => {
    const groups = new Map()
    points.forEach((point) => {
      // Separate visits at nearby coordinates as well as exact GPS duplicates.
      const nearby = [...groups.entries()].find(
        ([, group]) => distanceBetweenMarkers([group[0].gps, point.gps]) < 35
      )
      const key = nearby?.[0] ?? point.originalIndex
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(point)
    })
    const offsets = new Map()
    groups.forEach((group) =>
      group.forEach((point, i) => {
        const angle = (i / group.length) * Math.PI * 2
        offsets.set(
          point.originalIndex,
          group.length > 1
            ? {
                x: Math.cos(angle) * Math.max(36, group.length * 9) * markerSeparationScale,
                y: Math.sin(angle) * Math.max(36, group.length * 9) * markerSeparationScale
              }
            : { x: 0, y: 0 }
        )
      })
    )
    return offsets
  }, [points, markerSeparationScale])
  const selected = points[Math.min(index, points.length - 1)]
  const next = points[index + 1]
  const gap = Boolean(next && next.order - selected.order > 1)
  const status = selected ? statusOf(selected) : 'unknown'
  const geofence = resolveReportGeofence(selected?.activity)
  const distance = selected?.client ? distanceBetweenMarkers([selected.gps, selected.client]) : null
  const percent = points.length > 1 ? ((index + partial) / (points.length - 1)) * 100 : 0
  const position = selected
    ? next && !gap && !reduceMotion
      ? {
          lat: selected.gps.lat + (next.gps.lat - selected.gps.lat) * partial,
          lng: selected.gps.lng + (next.gps.lng - selected.gps.lng) * partial
        }
      : selected.gps
    : null

  useEffect(() => {
    setIndex(0)
    setPartial(0)
    setPlaying(false)
    setStarted(false)
  }, [data])
  useEffect(() => {
    const pause = () => {
      if (document.hidden) setPlaying(false)
    }
    document.addEventListener('visibilitychange', pause)
    return () => document.removeEventListener('visibilitychange', pause)
  }, [])
  useEffect(() => {
    setPlaying(false)
  }, [reduceMotion])
  useEffect(() => {
    if (ready) return undefined
    const timer = setTimeout(() => setError(true), 18000)
    return () => clearTimeout(timer)
  }, [ready, retry])

  const fit = () => {
    if (!api || !points.length) return
    overviewRef.current = true
    const bounds = new api.maps.LatLngBounds()
    points.forEach((point) => bounds.extend(point.gps))
    if (points.length === 1) {
      api.map.setCenter(points[0].gps)
      api.map.setZoom(16)
    } else {
      api.map.fitBounds(bounds, { top: isMobileView ? 155 : 140, right: 65, bottom: 110, left: 45 })
    }
  }
  useEffect(() => {
    if (!api) return undefined
    fit()
    setMapZoom(api.map.getZoom?.() ?? 0)
    const listener = api.maps.event.addListener(api.map, 'dragstart', () => {
      overviewRef.current = false
      setFollow(false)
    })
    const zoomListener = api.maps.event.addListener(api.map, 'zoom_changed', () => {
      setMapZoom(api.map.getZoom?.() ?? 0)
    })
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            api.maps.event.trigger(api.map, 'resize')
            if (overviewRef.current) fit()
          })
        : null
    if (canvasRef.current) observer?.observe(canvasRef.current)
    return () => {
      observer?.disconnect()
      api.maps.event.removeListener(listener)
      api.maps.event.removeListener(zoomListener)
    }
  }, [api, points])

  useEffect(() => {
    if (!api) return undefined
    const lines = points.slice(0, -1).map((point, i) => {
      const missing = points[i + 1].order - point.order > 1
      return new api.maps.Polyline({
        map: lineVisible && hasRouteMovement ? api.map : null,
        path: [point.gps, points[i + 1].gps],
        clickable: false,
        strokeColor: started && i < index ? '#27588d' : '#91abc5',
        strokeOpacity: missing ? 0 : 0.8,
        strokeWeight: 3,
        icons: missing
          ? [
              {
                icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.8, strokeColor: '#94a3b5', scale: 2 },
                offset: '0',
                repeat: '12px'
              }
            ]
          : undefined
      })
    })
    return () => lines.forEach((line) => line.setMap(null))
  }, [api, points, lineVisible, started, index, hasRouteMovement])

  useEffect(() => {
    if (!api || !selected) return undefined
    const circle = selected.client
      ? new api.maps.Circle({
          map: api.map,
          center: selected.client,
          radius: geofence.radius,
          strokeColor: colors[status],
          strokeOpacity: 0.8,
          strokeWeight: 1.5,
          fillColor: colors[status],
          fillOpacity: 0.12,
          clickable: false
        })
      : null
    circleRef.current = circle
    return () => {
      circle?.setMap(null)
      circleRef.current = null
    }
  }, [api, selected, geofence.radius, status])

  useEffect(() => {
    if (!api) return undefined
    const line = new api.maps.Polyline({
      map: lineVisible && hasRouteMovement ? api.map : null,
      strokeColor: '#27588d',
      strokeWeight: 4,
      clickable: false
    })
    progressRef.current = line
    return () => {
      line.setMap(null)
      progressRef.current = null
    }
  }, [api, lineVisible, hasRouteMovement])
  useEffect(() => {
    progressRef.current?.setPath(
      started && !gap && selected && next ? [selected.gps, position] : []
    )
    // setCenter avoids restarting an asynchronous pan on every animation frame.
    if (playing && cameraReady && follow && position && !gap && !reduceMotion)
      api?.map.setCenter(position)
  }, [partial, index, playing, cameraReady, follow, api, gap, started, lineVisible])

  // Finish framing the active visit before moving the vehicle. Cancel on pause/unmount.
  useEffect(() => {
    setCameraReady(false)
    if (!playing || !api || !selected) return undefined
    if (!follow || reduceMotion) {
      if (follow) api.map.setCenter(selected.gps)
      setCameraReady(true)
      return undefined
    }
    const center = api.map.getCenter?.()
    const origin = center ? { lat: center.lat(), lng: center.lng() } : selected.gps
    const target = position
    const initialZoom = api.map.getZoom?.() ?? 16
    const began = performance.now()
    let frame
    const frameCamera = (now) => {
      const fraction = Math.min(1, (now - began) / 1000)
      const eased = fraction * fraction * (3 - 2 * fraction)
      api.map.setCenter({
        lat: origin.lat + (target.lat - origin.lat) * eased,
        lng: origin.lng + (target.lng - origin.lng) * eased
      })
      api.map.setZoom(initialZoom + (16 - initialZoom) * eased)
      if (fraction < 1) frame = requestAnimationFrame(frameCamera)
      else setCameraReady(true)
    }
    frame = requestAnimationFrame(frameCamera)
    return () => cancelAnimationFrame(frame)
  }, [playing, follow, api, reduceMotion])

  useEffect(() => {
    if (!playing || !cameraReady || !ready || !next) return undefined
    const meters = distanceBetweenMarkers([selected.gps, next.gps]) || 0
    const duration = gap || reduceMotion ? 2400 : Math.max(3000, Math.min(14000, meters * 10))
    let last = performance.now()
    let frame
    const tick = (now) => {
      const elapsed = Math.min(now - last, 100)
      last = now
      setPartial((value) => Math.min(1, value + (elapsed * speed) / duration))
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [playing, cameraReady, ready, index, speed, next, selected, gap, reduceMotion])

  useEffect(() => {
    if (partial < 1) return
    setIndex((value) => value + 1)
    setPartial(0)
    if (index + 1 >= points.length - 1) setPlaying(false)
  }, [partial, index, points.length])

  const focus = (gps) => {
    if (!api || !gps) return
    overviewRef.current = false
    api.map.panTo(gps)
    api.map.setZoom(17)
  }
  const seek = (value) => {
    const target = Math.max(0, Math.min(value, points.length - 1))
    setPlaying(false)
    setIndex(target)
    setPartial(0)
    focus(points[target]?.gps)
  }
  const togglePlay = () => {
    if (!hasRouteMovement) return
    overviewRef.current = false
    if (!playing && index >= points.length - 1) {
      setIndex(0)
      setPartial(0)
    }
    if (!started || index >= points.length - 1) setFollow(true)
    setStarted(true)
    setPlaying((value) => !value)
  }
  const restart = () => {
    setPlaying(false)
    setStarted(false)
    setIndex(0)
    setPartial(0)
    fit()
  }
  const stationaryRouteMessage =
    'No hay recorrido para reproducir: las ubicaciones registradas permanecen dentro de un radio de 75 m.'

  // Keep static markers and their tooltips out of the per-frame playback render.
  const activityMarkers = useMemo(
    () =>
      points.map((point, i) => (
        <SalesRoutePoint
          key={point.originalIndex}
          lat={point.gps.lat}
          lng={point.gps.lng}
          point={point}
          number={clientNumber(point)}
          color={colors[statusOf(point)]}
          offset={markerOffsets.get(point.originalIndex)}
          selected={index === i}
          onSelect={() => {
            seek(i)
            setCollapsed(false)
            setPanel('detail')
          }}
        />
      )),
    [points, index, markerOffsets, api]
  )

  if (!selected)
    return (
      <div className="route-empty">
        <MapPin size={32} />
        <h3>Sin ubicaciones disponibles</h3>
        <p>No hay coordenadas GPS válidas para mostrar este recorrido.</p>
      </div>
    )
  return (
    <section
      className={`route-explorer ${collapsed ? 'route-explorer--collapsed' : ''} ${expanded ? 'route-explorer--expanded' : ''}`}
      aria-label="Recorrido del vendedor"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && expanded) {
          event.stopPropagation()
          setExpanded(false)
        }
      }}
    >
      <div className="route-workspace">
        <div className="route-canvas" ref={canvasRef}>
          <GoogleMapReact
            key={retry}
            bootstrapURLKeys={{ key: API_KEY }}
            defaultCenter={points[0].gps}
            defaultZoom={14}
            yesIWantToUseGoogleMapApiInternals
            options={{
              styles: modalMapStyles,
              fullscreenControl: false,
              mapTypeControl: false,
              streetViewControl: false,
              zoomControl: true,
              gestureHandling: isMobileView ? 'greedy' : 'auto',
              maxZoom: 20
            }}
            onGoogleApiLoaded={({ map, maps }) => {
              setApi({ map, maps })
              setReady(true)
              setError(false)
            }}
          >
            {activityMarkers}
            {selected.client && (
              <Tooltip
                lat={selected.client.lat}
                lng={selected.client.lng}
                title={`Cliente: ${selected.activity.client_name || 'Cliente sin nombre'} · ${geofence.source === 'fallback' ? 'Radio de referencia' : 'Geocerca'}: ${geofence.radius} m`}
                arrow
                describeChild
                slotProps={{ popper: { sx: { zIndex: 2300 } } }}
              >
                <button
                  lat={selected.client.lat}
                  lng={selected.client.lng}
                  className="route-client"
                  type="button"
                  aria-label="Ubicación del cliente"
                  onClick={() => focus(selected.client)}
                >
                  <MapPin size={18} weight="fill" />
                </button>
              </Tooltip>
            )}
            {started && !gap && (
              <div
                lat={position.lat}
                lng={position.lng}
                className={`route-van ${playing ? 'route-van--moving' : ''}`}
                aria-label="Posición en la reproducción"
              >
                <div className="route-van-aura" />
                <img
                  src={salesRouteVan}
                  alt=""
                  style={{
                    transform: `rotate(${bearing(next ? selected.gps : points[index - 1]?.gps, next?.gps || selected.gps)}deg)`
                  }}
                />
              </div>
            )}
          </GoogleMapReact>
          {!ready && (
            <div className="route-loading" role="status">
              <MapPin size={28} />
              <p>{error ? 'No se pudo cargar el mapa. Revisa tu conexión.' : 'Cargando mapa…'}</p>
              {error && (
                <button
                  type="button"
                  onClick={() => {
                    setApi(null)
                    setError(false)
                    setRetry((value) => value + 1)
                  }}
                >
                  Reintentar
                </button>
              )}
            </div>
          )}
          <div className="route-map-heading">
            <div className="route-map-info-header">
              <strong>Mapa de ubicación</strong>
              <button
                className="route-map-info-toggle"
                type="button"
                aria-expanded={mapInfoOpen}
                aria-controls={mapInfoId}
                aria-label={
                  mapInfoOpen ? 'Ocultar información del mapa' : 'Mostrar información del mapa'
                }
                onClick={() => setMapInfoOpen((value) => !value)}
              >
                <CaretDown size={16} aria-hidden="true" />
              </button>
            </div>
            <div
              id={mapInfoId}
              className="route-map-info-body"
              data-open={mapInfoOpen}
              style={{ visibility: mapInfoOpen ? 'visible' : 'hidden' }}
              aria-hidden={!mapInfoOpen}
              inert={!mapInfoOpen}
            >
              <div className="route-map-info-content">
                <small>
                  {points.length} actividades con GPS · {time(points[0].activity)} —{' '}
                  {time(points.at(-1).activity)}
                </small>
                <div className="route-map-actions">
                  {hasRouteMovement && (
                    <button
                      disabled={!ready}
                      type="button"
                      aria-pressed={lineVisible}
                      onClick={() => setLineVisible((value) => !value)}
                    >
                      <Path size={16} /> {lineVisible ? 'Ocultar ruta' : 'Mostrar ruta'}
                    </button>
                  )}
                  <button
                    disabled={!ready}
                    type="button"
                    onClick={() => {
                      setFollow(false)
                      fit()
                    }}
                  >
                    <Crosshair size={16} /> Ver todo
                  </button>
                </div>
                <SalesRouteLegend />
              </div>
            </div>
          </div>
          <button
            className="route-expand"
            type="button"
            aria-label={expanded ? 'Salir de pantalla completa' : 'Pantalla completa'}
            aria-pressed={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            <CornersOut size={19} />
          </button>
          {gap && started && (
            <div className="route-gap" role="status">
              Intervalo sin registros GPS
            </div>
          )}
        </div>
        <aside className="route-inspector" aria-label="Información de la actividad">
          <div className="route-panel-tabs">
            <button
              type="button"
              className="route-panel-toggle"
              aria-label={collapsed ? 'Expandir información' : 'Contraer información'}
              aria-expanded={!collapsed}
              onClick={() => setCollapsed((value) => !value)}
            >
              {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
            </button>
            <button
              type="button"
              aria-pressed={panel === 'detail'}
              onClick={() => {
                setPanel('detail')
              }}
            >
              Detalle
            </button>
            <button
              type="button"
              aria-pressed={panel === 'list'}
              onClick={() => {
                setPanel('list')
              }}
            >
              {points.length} actividades
            </button>
          </div>
          <div className="route-panel-content" hidden={collapsed}>
            {panel === 'list' ? (
              <>
                <p className="route-muted">{missingGpsCount} registros sin GPS</p>
                {excludedGpsCount > 0 && (
                  <p className="route-muted">
                    {excludedGpsCount} coordenada{excludedGpsCount === 1 ? '' : 's'} fuera del área
                    esperada se omitió del recorrido
                  </p>
                )}
                <ol className="route-visits">
                  {points.map((point, i) => (
                    <li key={point.originalIndex}>
                      {i > 0 && point.order - points[i - 1].order > 1 && (
                        <small className="route-muted">Intervalo sin GPS</small>
                      )}
                      <button
                        type="button"
                        aria-current={index === i ? 'step' : undefined}
                        onClick={() => {
                          seek(i)
                          setPanel('detail')
                        }}
                      >
                        <b style={{ color: colors[statusOf(point)] }}>{clientNumber(point)}</b>
                        <span>
                          {point.activity.client_name || 'Cliente'}
                          <small>
                            {time(point.activity)} · {statusText[statusOf(point)]}
                          </small>
                        </span>
                        <CaretRight />
                      </button>
                    </li>
                  ))}
                </ol>
              </>
            ) : (
              <>
                <div className="route-record-heading">
                  <span
                    style={{
                      '--route-record-color': colors[status],
                      '--route-record-text-color': 'white'
                    }}
                  >
                    {clientNumber(selected)}
                  </span>
                  <div>
                    <h3>{selected.activity.client_name || 'Cliente sin nombre'}</h3>
                    <small>
                      Actividad {index + 1} de {points.length}
                    </small>
                  </div>
                </div>
                <div className="route-record-meta">
                  <time>{time(selected.activity)}</time>
                  <span>{selected.activity.activity_name || 'Actividad'}</span>
                </div>
                <div className={`route-geofence route-geofence--${status}`}>
                  <strong>
                    {status === 'inside' ? <CheckCircle /> : <WarningCircle />}
                    {!selected.client ? 'Cliente sin ubicación registrada' : statusText[status]}
                  </strong>
                  <div>
                    <strong className="route-distance">
                      {distance === null ? '—' : `${Math.round(distance).toLocaleString()} m`}
                    </strong>
                    <small>hasta el cliente</small>
                  </div>
                  <div className="route-geofence-reference">
                    <small>
                      {selected.client
                        ? `${geofence.label}: ${geofence.radius} m`
                        : 'Sin ubicación del cliente'}
                    </small>
                    <small className="route-geofence-source">Distancia GPS</small>
                  </div>
                </div>
                <h4>Ubicación registrada</h4>
                {[
                  { label: 'Actividad', gps: selected.gps },
                  { label: 'Cliente', gps: selected.client }
                ].map((point) => (
                  <div className="route-location" key={point.label}>
                    <i className={point.label === 'Cliente' ? 'client' : ''} />
                    <div>
                      <strong>{point.label}</strong>
                      <small>
                        {point.gps
                          ? `${point.gps.lat.toFixed(6)}, ${point.gps.lng.toFixed(6)}`
                          : 'Sin coordenadas disponibles'}
                      </small>
                    </div>
                    <button
                      type="button"
                      disabled={!ready || !point.gps}
                      aria-label={`Enfocar ${point.label.toLowerCase()}`}
                      onClick={() => focus(point.gps)}
                    >
                      <Crosshair size={16} />
                    </button>
                  </div>
                ))}
                {selected.client && (
                  <button
                    className="route-fence-focus"
                    disabled={!ready}
                    type="button"
                    onClick={() => {
                      const bounds = circleRef.current?.getBounds()
                      if (bounds) {
                        overviewRef.current = false
                        api.map.fitBounds(bounds, 50)
                      }
                    }}
                  >
                    <Crosshair size={15} /> Enfocar geocerca · {geofence.radius} m
                  </button>
                )}
                <details>
                  <summary>Coordenadas y registro</summary>
                  <p>Fecha: {selected.activity.created_date || '—'}</p>
                  <p>Cliente en el reporte: {selected.activity.client_index ?? '—'}</p>
                  {selected.activity.total != null && <p>Monto: {selected.activity.total}</p>}
                </details>
                {next && (
                  <button className="route-next" type="button" onClick={() => seek(index + 1)}>
                    <span>
                      <small>Siguiente actividad · {time(next.activity)}</small>
                      {index + 2}. {next.activity.client_name || 'Cliente'}
                    </span>
                    <CaretRight />
                  </button>
                )}
              </>
            )}
          </div>
          <div className="route-report-notes" hidden={collapsed}>
            <strong>Acerca del recorrido</strong>
            <p>El número de cada marcador identifica a un cliente único en el período visitado.</p>
            <p>Los intervalos sin registros GPS se muestran con líneas discontinuas.</p>
            <p>
              Las líneas conectan ubicaciones registradas; no representan navegación por calles.
            </p>
          </div>
        </aside>
      </div>
      {/* Fuera del scope actual: reproducción del recorrido pendiente para la segunda versión.
      <footer className="route-player">
        {hasRouteMovement ? (
          <>
            <div className="route-player-top">
              <Tooltip
                title={!hasRouteMovement ? stationaryRouteMessage : ''}
                describeChild
                disableHoverListener={hasRouteMovement}
              >
                <span>
                  <button
                    className="route-play"
                    type="button"
                    disabled={!ready || !hasRouteMovement}
                    onClick={togglePlay}
                  >
                    {playing ? <Pause weight="fill" /> : <Play weight="fill" />}
                    {hasRouteMovement
                      ? playing
                        ? 'Pausar'
                        : started
                          ? index === points.length - 1
                            ? 'Volver a ver'
                            : 'Continuar'
                          : 'Ver recorrido'
                      : 'Sin recorrido'}
                  </button>
                </span>
              </Tooltip>
              <div className="route-transport">
                <button
                  type="button"
                  disabled={!hasRouteMovement || index === 0}
                  aria-label="Actividad anterior"
                  onClick={() => seek(index - 1)}
                >
                  <SkipBack />
                </button>
                <button
                  type="button"
                  disabled={!hasRouteMovement || index === points.length - 1}
                  aria-label="Actividad siguiente"
                  onClick={() => seek(index + 1)}
                >
                  <SkipForward />
                </button>
                <button
                  type="button"
                  disabled={!hasRouteMovement}
                  aria-label="Reiniciar recorrido"
                  onClick={restart}
                >
                  <ArrowCounterClockwise />
                </button>
              </div>
              <div className="route-speeds" aria-label="Velocidad de reproducción">
                {[0.5, 1, 1.5, 2].map((value) => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={speed === value}
                    onClick={() => setSpeed(value)}
                  >
                    {value}x
                  </button>
                ))}
              </div>
              <div className="route-play-status" aria-live="polite">
                <strong>
                  {!hasRouteMovement
                    ? 'Sin desplazamiento verificable'
                    : playing
                      ? `Actividad ${index + 1} · ${time(selected.activity)}`
                      : index === points.length - 1 && started
                        ? 'Recorrido finalizado'
                        : started
                          ? 'Reproducción en pausa'
                          : 'Tu día, punto a punto'}
                </strong>
                <small>
                  {!hasRouteMovement
                    ? 'Las ubicaciones GPS permanecen dentro de un radio de 75 m.'
                    : playing
                      ? selected.activity.client_name
                      : 'Explora la secuencia de ubicaciones registradas.'}
                </small>
              </div>
              <button
                className="route-follow"
                type="button"
                aria-pressed={follow}
                onClick={() => {
                  setFollow((value) => !value)
                  if (!follow && !playing) focus(position)
                }}
              >
                <Crosshair size={14} /> Seguir vehículo <span />
              </button>
              <small>{Math.round(percent)} %</small>
            </div>
            <input
              className="route-scrubber"
              type="range"
              min={0}
              max={Math.max(0, points.length - 1)}
              step="0.001"
              value={index + partial}
              disabled={!hasRouteMovement}
              aria-label="Seleccionar actividad del recorrido"
              aria-valuetext={`Actividad ${index + 1} de ${points.length}: ${selected.activity.client_name || 'Cliente'}`}
              onChange={(event) => seek(Math.round(Number(event.target.value)))}
              style={{ '--progress': `${percent}%` }}
            />
            <div className="route-timeline-labels">
              <span>{time(points[0].activity)}</span>
              <span>
                {points.length} actividades · {missingGpsCount} registros sin GPS
                {excludedGpsCount > 0
                  ? ` · ${excludedGpsCount} coordenada${excludedGpsCount === 1 ? '' : 's'} omitida${excludedGpsCount === 1 ? '' : 's'}`
                  : ''}
              </span>
              <span>{time(points.at(-1).activity)}</span>
            </div>
          </>
        ) : (
          <div className="route-stationary-notice" role="status">
            <Crosshair size={18} />
            <div>
              <strong>Sin desplazamiento verificable</strong>
              <small>
                {stationaryRouteMessage}
                {excludedGpsCount > 0
                  ? ` · ${excludedGpsCount} coordenada${excludedGpsCount === 1 ? '' : 's'} omitida${excludedGpsCount === 1 ? '' : 's'}`
                  : ''}
              </small>
            </div>
          </div>
        )}
      </footer>
      */}
    </section>
  )
}
